/**
 * Read the user's active Prompt Lab session in full.
 *
 * Bespoke alternative to readWindowContext('/prompt-lab') — that bridge
 * returns metadata only (active id, status, specialization name). This
 * tool returns the actual systemPrompt, userPrompt, and skillPrompts
 * the user is composing so NIPE can critique, summarize, or suggest
 * edits.
 *
 * Pairs with `setSystemPrompt` / `setUserPrompt` which mutate via the
 * existing Redux `updateSession` action.
 */

import type { NipeTool } from './types';

export interface PromptSessionSnapshot {
  sessionId: string;
  systemPrompt: string;
  userPrompt: string;
  skillPrompts: ReadonlyArray<{
    id: string;
    name: string;
    content: string;
    weight: number;
    enabled: boolean;
  }>;
  modelId: string;
  status: string;
  selectedSpecializationName: string | null;
}

export interface ReadPromptSessionContext {
  /** Returns the active session snapshot or null when none exists yet
   * (Redux default-session effect mid-flight). */
  readPromptSession?: () => PromptSessionSnapshot | null;
}

export const readPromptSessionTool: NipeTool<ReadPromptSessionContext> = {
  schema: {
    type: 'function',
    function: {
      name: 'readPromptSession',
      description:
        "Read the user's active Prompt Lab session in full — system prompt, " +
        'user prompt, every skill prompt with weights + enabled state, the ' +
        'model id, and any specialization loaded. Use this when the user ' +
        'asks you to look at, critique, summarize, or improve their ' +
        'in-progress prompt. The cheap metadata-only alternative is ' +
        'readWindowContext("/prompt-lab"); reach for THIS tool when you ' +
        'actually need the body text.',
      parameters: {
        type: 'object',
        properties: {},
        required: [],
      },
    },
  },
  mutatesRemote: false,
  requiresApproval: false,
  handler: async (_args, ctx) => {
    if (!ctx.readPromptSession) {
      return {
        available: false,
        reason: 'Prompt Lab is not mounted in this session.',
      };
    }
    const snap = ctx.readPromptSession();
    if (snap === null) {
      return {
        available: false,
        reason: 'No active prompt session yet (Prompt Lab is still booting).',
      };
    }
    return { available: true, session: snap };
  },
};
